import { useEffect, useState } from "react";

import { Button, Icon, SettingsList, SettingsListItem, Text, View } from "../base";
import { OrganizationSettingsTab } from "./OrganizationSettingTab";
import { sessionActions } from "../../redux/reducers/sessionSlice";
import { useApiRequest } from "../../hooks/useApiRequest";
import { useAppDispatch } from "../../redux";

type UserInvite = {
  id: string;
  organizationId: string;
  organizationName: string;
  email: string;
  role: string;
  status: string;
  expiresAt: string;
  createdAt: string;
  updatedAt: string;
};

export const InvitesCard = () => {
  const dispatch = useAppDispatch();
  const listInvitesRequest = useApiRequest<{ data: UserInvite[] }>();
  const acceptRequest = useApiRequest();
  const declineRequest = useApiRequest();
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadInvites = () => listInvitesRequest.fetch("/v1/me/invites");

  useEffect(() => {
    loadInvites();
  }, []);

  const invites = listInvitesRequest.data?.data ?? [];

  if (!invites.length) {
    return null;
  }

  const onAccept = async (invite: UserInvite) => {
    setPendingId(invite.id);
    setError(null);
    const { error: reqError } = await acceptRequest.fetch(`/v1/me/invites/${invite.id}/accept`, {
      method: "POST",
    });
    if (reqError) {
      setError(reqError.message || "Could not accept invite");
      setPendingId(null);
      return;
    }
    await Promise.all([dispatch(sessionActions.getSession()), loadInvites()]);
    setPendingId(null);
  };

  const onDecline = async (invite: UserInvite) => {
    setPendingId(invite.id);
    setError(null);
    const { error: reqError } = await declineRequest.fetch(`/v1/me/invites/${invite.id}/decline`, {
      method: "POST",
    });
    if (reqError) {
      setError(reqError.message || "Could not decline invite");
      setPendingId(null);
      return;
    }
    await loadInvites();
    setPendingId(null);
  };

  return (
    <OrganizationSettingsTab
      title="Pending Invites"
      count={invites.length}
    >
      <SettingsList>
        {invites.map((invite) => {
          const isPending = pendingId === invite.id;
          return (
            <SettingsListItem
              key={invite.id}
              icon={invite.organizationName?.charAt(0)?.toUpperCase() || "O"}
              title={invite.organizationName}
              subtitle={
                <View className="flex flex-row items-center gap-2">
                  <Text className="text-xs uppercase tracking-wider text-gray-400 dark:text-gray-500">{invite.role}</Text>
                  <Text className="text-xs text-gray-400 dark:text-gray-500">• Expires {new Date(invite.expiresAt).toLocaleDateString()}</Text>
                </View>
              }
              right={
                <View className="flex items-center gap-2">
                  <Button
                    variant="neutral"
                    size="auto"
                    onClick={() => onDecline(invite)}
                    isDisabled={!!pendingId}
                  >
                    {isPending && declineRequest.isLoading ? "Declining…" : "Decline"}
                  </Button>
                  <Button
                    variant="success"
                    size="auto"
                    onClick={() => onAccept(invite)}
                    isDisabled={!!pendingId}
                  >
                    <Icon
                      name="check"
                      size={14}
                    />
                    {isPending && acceptRequest.isLoading ? "Accepting…" : "Accept"}
                  </Button>
                </View>
              }
            />
          );
        })}
      </SettingsList>
      {error && <Text className="mt-2 text-xs text-red-500 dark:text-red-400">{error}</Text>}
    </OrganizationSettingsTab>
  );
};
